import { useCallback } from 'react';
// Local files
import sound from 'assets/notification.mp3';
import { detectOS } from 'helpers';
import useSuccess from 'hooks/useSuccess';

const audio = new Audio(sound);

const useNotification = () => {
  const { setSuccess } = useSuccess();
  const handleNotificationShow = () => detectOS() === 'Mac OS' && audio.play().catch((error) => console.log(error));

  const sendNotification = useCallback(async (body, id, type, silent = true) => {
    try {
      // Try direct Notification API first (works when no service worker is active)
      let notification;
      try {
        notification = new Notification('Order', { body, silent });
        
        setTimeout(() => notification.close(), 5000);

        notification.onclick = (event) => { 
          event.preventDefault();
          window.open(`/${type}/${id}`, '_self');
        };
        if (!silent) {
          notification.onshow = handleNotificationShow;
        }
        return;
      } catch (directError) {
        // If direct Notification fails with "Illegal constructor" error,
        // it means a service worker is active and we need to use ServiceWorkerRegistration
        if (directError.message && directError.message.includes('Illegal constructor')) {
          if ('serviceWorker' in navigator) {
            try {
              const registration = await navigator.serviceWorker.ready;
              if (registration) {
                // Use ServiceWorkerRegistration.showNotification() when service worker is active
                const notificationOptions = {
                  body,
                  silent,
                  tag: `order-${id}`,
                  data: { url: `/${type}/${id}` }
                };
                
                await registration.showNotification('Order', notificationOptions);
                
                // Auto-close after 5 seconds
                setTimeout(async () => {
                  try {
                    const reg = await navigator.serviceWorker.ready;
                    if (reg) {
                      const notifications = await reg.getNotifications({ tag: `order-${id}` });
                      notifications.forEach(notif => notif.close());
                    }
                  } catch (error) {
                    console.log('Error closing notification:', error);
                  }
                }, 5000);
                
                if (!silent) {
                  handleNotificationShow();
                }
                return;
              }
            } catch (swError) {
              console.error('Error using service worker notification:', swError);
              // Fall through to setSuccess
            }
          }
        }
        // Re-throw if it's not the "Illegal constructor" error
        throw directError;
      }
    } catch (error) {
      // If notification fails, fall back to success message
      console.error('Error showing notification:', error);
      setSuccess(body);
    }
  }, [setSuccess]);
  
  const showNotification = useCallback(({ body, id, type, silent = true }) => {
    if(!("Notification" in window)) {
      setSuccess(body);
    } 
    else if(Notification.permission === 'granted') {
      sendNotification(body, id, type, silent);
    }
    else if(Notification.permission !== 'denied') {
       Notification.requestPermission((permission)=> {
          if (permission === "granted") {
            sendNotification(body, id, type, silent)
          } else {
            setSuccess(body);
          }
       })
    } else {
      setSuccess(body);
    }
  }, [sendNotification, setSuccess]);

  return { showNotification };
};

export default useNotification;